import { createAction, props } from '@ngrx/store';

import {
  Contact,
  Proposal,
  Quote,
  QuoteAddon,
  QuoteOption,
  QuoteResponseAction,
} from '@lockton-apps/shared/interfaces';

import { QuotesApiActionTypes } from './actions.model';

// Load Quotes
export const loadQuotesSuccess = createAction(
  QuotesApiActionTypes.LoadQuotesSuccess,
  props<{ quotes: Quote[] }>()
);

export const loadQuotesFailure = createAction(
  QuotesApiActionTypes.LoadQuotesFailure,
  props<{ error: unknown }>()
);

// Load Quote
export const loadQuoteSuccess = createAction(
  QuotesApiActionTypes.LoadQuoteSuccess,
  props<{ quote: Quote }>()
);

export const loadQuoteFailure = createAction(
  QuotesApiActionTypes.LoadQuoteFailure,
  props<{ error: unknown }>()
);

export const loadQuoteWithPropFormSuccess = createAction(
  QuotesApiActionTypes.LoadQuoteWithPropFormSuccess,
  props<{ quote: Quote }>()
);

// Load Client Quotes

/** Quotes API event triggered on successful load of client quotes  */
export const loadClientQuotesSuccess = createAction(
  QuotesApiActionTypes.LoadClientQuotesSuccess,
  props<{ quotes: Quote[] }>()
);

/** Quotes API event triggered on successful load of client quotes.
 *  This action also indicates follow up event to check for any love
 *  quotes
 */
export const loadClientQuotesWithLiveCheckSuccess = createAction(
  QuotesApiActionTypes.LoadClientQuotesWithLiveCheckSuccess,
  props<{ quotes: Quote[] }>()
);

/** Quotes API event triggered on failed load attempt of client quotes  */
export const loadClientQuotesFailure = createAction(
  QuotesApiActionTypes.LoadClientQuotesFailure,
  props<{ error: unknown }>()
);

/** Quotes API event triggered on successful load of quotes for contact dashboard.
 */
export const loadQuotesForContactDashboardSuccess = createAction(
  QuotesApiActionTypes.LoadQuotesForContactDashboardSuccess,
  props<{ quotes: Quote[] }>()
);

/** Quotes API event triggered on failed load attempt of quotes for contact dashboard */
export const loadQuotesForContactDashboardFailure = createAction(
  QuotesApiActionTypes.LoadQuotesForContactDashboardFailure,
  props<{ error: unknown }>()
);

// Quote Approved

export const quoteApprovedSuccess = createAction(
  QuotesApiActionTypes.QuoteApprovedSuccess,
  props<{ quote: Quote }>()
);

export const quoteApprovedFailure = createAction(
  QuotesApiActionTypes.QuoteApprovedFailure,
  props<{ error: any }>()
);

// Changes Requested

export const changesRequestedSuccess = createAction(
  QuotesApiActionTypes.ChangesRequestedSuccess,
  props<{ quote: Quote }>()
);

export const changesRequestedFailure = createAction(
  QuotesApiActionTypes.ChangesRequestedFailure,
  props<{ error: any }>()
);

// Broker Assigned

export const brokerAssignedSuccess = createAction(
  QuotesApiActionTypes.BrokerAssignedSuccess
);

export const brokerAssignedFailure = createAction(
  QuotesApiActionTypes.BrokerAssignedFailure,
  props<{ error: any }>()
);

// Quote Released
export const releasedSuccess = createAction(
  QuotesApiActionTypes.QuoteReleasedSuccess,
  props<{ quote: Quote }>()
);

export const releasedFailure = createAction(
  QuotesApiActionTypes.QuoteReleasedFailure,
  props<{ error: any }>()
);

// Quote Completed
export const completedSuccess = createAction(
  QuotesApiActionTypes.QuoteCompletedSuccess,
  props<{ quotes: Quote[] }>()
);

export const completedFailure = createAction(
  QuotesApiActionTypes.QuoteCompletedFailure,
  props<{ error: any }>()
);

export const updateStatusSuccess = createAction(
  QuotesApiActionTypes.UpdateStatusSuccess,
  props<{ quote: Quote }>()
);

export const updateStatusFailure = createAction(
  QuotesApiActionTypes.UpdateStatusFailure,
  props<{ error: any }>()
);

export const endorsementSubmittedSuccess = createAction(
  QuotesApiActionTypes.EndorsementSubmittedSuccess,
  props<{ quote: Quote }>()
);

export const endorsementSubmittedFailure = createAction(
  QuotesApiActionTypes.EndorsementSubmittedFailure,
  props<{ error: any }>()
);

export const tierSelectedSuccess = createAction(
  QuotesApiActionTypes.TierSelectedSuccess,
  props<{ option: QuoteOption }>()
);

export const tierSelectedFailure = createAction(
  QuotesApiActionTypes.TierSelectedFailure,
  props<{ error: any }>()
);

export const tierAdjustedSuccess = createAction(
  QuotesApiActionTypes.TierAdjustedSuccess,
  props<{ option: QuoteOption }>()
);

export const tierAdjustedFailure = createAction(
  QuotesApiActionTypes.TierAdjustedFailure,
  props<{ error: any }>()
);

export const adjustmentsAppliedSuccess = createAction(
  QuotesApiActionTypes.AdjustmentsAppliedSuccess,
  props<{ quote: Quote }>()
);

export const adjustmentsAppliedFailure = createAction(
  QuotesApiActionTypes.AdjustmentsAppliedFailure,
  props<{ error: any }>()
);

export const endorsementsUpdatedSuccess = createAction(
  QuotesApiActionTypes.EndorsementsUpdatedSuccess,
  props<{ quote: Quote }>()
);

export const endorsementsUpdatedFailure = createAction(
  QuotesApiActionTypes.EndorsementsUpdatedFailure,
  props<{ error: any }>()
);

export const addonToggledSuccess = createAction(
  QuotesApiActionTypes.AddonToggledSuccess,
  props<{ addon: QuoteAddon }>()
);

export const addonToggledFailure = createAction(
  QuotesApiActionTypes.AddonToggledFailure,
  props<{ error: any }>()
);

export const addonAdjustedSuccess = createAction(
  QuotesApiActionTypes.AddonAdjustedSuccess,
  props<{ addon: QuoteAddon }>()
);

export const addonAdjustedFailure = createAction(
  QuotesApiActionTypes.AddonAdjustedFailure,
  props<{ error: any }>()
);

export const initialisedSuccess = createAction(
  QuotesApiActionTypes.InitialisedSuccess,
  props<{ quotes: Quote[] }>()
);

export const initialisedFailure = createAction(
  QuotesApiActionTypes.InitialisedFailure,
  props<{ error: any }>()
);

export const reconfiguredSuccess = createAction(
  QuotesApiActionTypes.ReconfiguredSuccess,
  props<{ quotes: Quote[] }>()
);

export const reconfiguredFailure = createAction(
  QuotesApiActionTypes.ReconfiguredFailure,
  props<{ error: any }>()
);

export const checklistUpdatedSuccess = createAction(
  QuotesApiActionTypes.ChecklistUpdatedSuccess,
  props<{ quote: Quote }>()
);

export const checklistUpdatedFailure = createAction(
  QuotesApiActionTypes.ChecklistUpdatedFailure,
  props<{ quote: Quote; error: any }>()
);

export const proposalUpdatedSuccess = createAction(
  QuotesApiActionTypes.ProposalUpdatedSuccess,
  props<{ responses: QuoteResponseAction[]; quote: Quote }>()
);

export const proposalUpdatedFailure = createAction(
  QuotesApiActionTypes.ProposalUpdatedFailure,
  props<{ error: any }>()
);

export const proposalStepSubmitSuccess = createAction(
  QuotesApiActionTypes.ProposalStepSubmitSuccess,
  props<{ proposal: Proposal }>()
);

export const proposalAutoSavedSuccess = createAction(
  QuotesApiActionTypes.ProposalAutoSavedSuccess,
  props<{ responses: QuoteResponseAction[]; quote: Quote }>()
);

export const proposalAutoSavedFailure = createAction(
  QuotesApiActionTypes.ProposalAutoSavedFailure,
  props<{ error: any }>()
);

export const proposalBackupSavedSuccess = createAction(
  QuotesApiActionTypes.ProposalBackupSavedSuccess,
  props<{ responses: QuoteResponseAction[]; quote: Quote }>()
);

export const proposalBackupSavedFailure = createAction(
  QuotesApiActionTypes.ProposalBackupSavedFailure,
  props<{ error: any }>()
);

export const proposalSubmittedSuccess = createAction(
  QuotesApiActionTypes.ProposalSubmittedSuccess,
  props<{ responses: QuoteResponseAction[]; quote: Quote }>()
);

export const proposalSubmittedFailure = createAction(
  QuotesApiActionTypes.ProposalSubmittedFailure,
  props<{ error: any; proposal?: Proposal }>()
);

export const sendRenewalInviteSuccess = createAction(
  QuotesApiActionTypes.SendRenewalInviteSuccess,
  props<{ responses: QuoteResponseAction[]; quote: Quote }>()
);

export const sendRenewalInviteFailure = createAction(
  QuotesApiActionTypes.SendRenewalInviteFailure,
  props<{ error: any }>()
);

export const proposalReturnedSuccess = createAction(
  QuotesApiActionTypes.ProposalReturnedSuccess,
  props<{ responses: QuoteResponseAction[]; quote: Quote }>()
);

export const proposalReturnedFailure = createAction(
  QuotesApiActionTypes.ProposalReturnedFailure,
  props<{ error: any }>()
);

export const holdSuccess = createAction(
  QuotesApiActionTypes.HoldSuccess,
  props<{ responses: QuoteResponseAction[]; quote: Quote }>()
);

export const holdFailure = createAction(
  QuotesApiActionTypes.HoldFailure,
  props<{ error: any }>()
);

export const requotedSuccess = createAction(
  QuotesApiActionTypes.RequotedSuccess,
  props<{ responses: QuoteResponseAction[]; quote: Quote }>()
);

export const requotedFailure = createAction(
  QuotesApiActionTypes.RequotedFailure,
  props<{ error: any }>()
);

export const sendToClientSuccess = createAction(
  QuotesApiActionTypes.SendToClientSuccess,
  props<{ responses: QuoteResponseAction[]; quote: Quote }>()
);

export const sendToClientFailure = createAction(
  QuotesApiActionTypes.SendToClientFailure,
  props<{ error: any }>()
);

export const acceptSuccess = createAction(
  QuotesApiActionTypes.AcceptSuccess,
  props<{ responses: QuoteResponseAction[]; quote: Quote }>()
);

export const acceptFailure = createAction(
  QuotesApiActionTypes.AcceptFailure,
  props<{ error: any }>()
);

export const setPaymentTypeSuccess = createAction(
  QuotesApiActionTypes.SetPaymentTypeSuccess,
  props<{ responses: QuoteResponseAction[]; quote: Quote }>()
);

export const setPaymentTypeFailure = createAction(
  QuotesApiActionTypes.SetPaymentTypeFailure,
  props<{ error: any }>()
);

export const invoiceSuccess = createAction(
  QuotesApiActionTypes.InvoiceSuccess,
  props<{ responses: QuoteResponseAction[]; quote: Quote }>()
);

export const invoiceFailure = createAction(
  QuotesApiActionTypes.InvoiceFailure,
  props<{ error: any }>()
);

export const paymentReceivedSuccess = createAction(
  QuotesApiActionTypes.PaymentReceivedSuccess,
  props<{ responses: QuoteResponseAction[]; quote: Quote }>()
);

export const paymentReceivedFailure = createAction(
  QuotesApiActionTypes.PaymentReceivedFailure,
  props<{ error: any }>()
);

export const completeSuccess = createAction(
  QuotesApiActionTypes.CompleteSuccess,
  props<{ responses: QuoteResponseAction[]; quote: Quote }>()
);

export const completeFailure = createAction(
  QuotesApiActionTypes.CompleteFailure,
  props<{ error: any }>()
);

export const quoteRequestedSuccess = createAction(
  QuotesApiActionTypes.QuoteRequestedSuccess,
  props<{ responses: QuoteResponseAction[]; quote: Quote }>()
);

export const quoteRequestedFailure = createAction(
  QuotesApiActionTypes.QuoteRequestedFailure,
  props<{ error: any }>()
);

export const quoteReadySuccess = createAction(
  QuotesApiActionTypes.QuoteReadySuccess,
  props<{ responses: QuoteResponseAction[]; quote: Quote }>()
);

export const quoteReadyFailure = createAction(
  QuotesApiActionTypes.QuoteReadyFailure,
  props<{ error: any }>()
);

export const notTakenUpSuccess = createAction(
  QuotesApiActionTypes.NotTakenUpSuccess,
  props<{ responses: QuoteResponseAction[]; quote: Quote }>()
);

export const notTakenUpFailure = createAction(
  QuotesApiActionTypes.NotTakenUpFailure,
  props<{ error: any }>()
);

export const reconfigured2Success = createAction(
  QuotesApiActionTypes.Reconfigured2Success,
  props<{ responses: QuoteResponseAction[]; quote: Quote }>()
);

export const reconfigured2Failure = createAction(
  QuotesApiActionTypes.Reconfigured2Failure,
  props<{ error: any }>()
);

export const contactsAssignedSuccess = createAction(
  QuotesApiActionTypes.ContactsAssignedSuccess,
  props<{ quote: Quote }>()
);
